// Isometric room renderer: draws the pixel-art room built in roomart.ts, plus animated glows.
import { FLAT_KINDS, type Furni } from "../shared/protocol";
import type { Bitmap } from "./pixel";
import { buildBackground, buildFurni, buildHover, OX, OY, P, type FurniArt } from "./roomart";

export const TW = 64; // tile width
export const TH = 32; // tile height
export const WALL_H = 110;

/** Grid coords (can be fractional) -> world pixel coords, origin at tile (0,0)'s top corner. */
export const iso = (gx: number, gy: number): [number, number] => [(gx - gy) * (TW / 2), (gx + gy) * (TH / 2)];

/** World pixel coords -> a spot on the back wall (r 0, along x) or left wall (r 1, along y), or null. */
export function wallAt(wx: number, wy: number, size: number): { r: 0 | 1; pos: number } | null {
  const along = Math.abs(wx) / (TW / 2);
  const z = along * (TH / 2) - wy; // height above the floor line
  if (z < 0 || z > WALL_H || along >= size) return null;
  return { r: wx >= 0 ? 0 : 1, pos: Math.floor(along) };
}

/** World pixel coords -> grid tile. */
export function tileAt(wx: number, wy: number): [number, number] {
  const a = wy / (TH / 2), b = wx / (TW / 2);
  return [Math.floor((a + b) / 2), Math.floor((a - b) / 2)];
}

function toCanvas(b: Bitmap): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = b.w;
  c.height = b.h;
  c.getContext("2d")!.putImageData(new ImageData(new Uint8ClampedArray(b.data), b.w, b.h), 0, 0);
  return c;
}

const bgCache = new Map<string, HTMLCanvasElement>();
const furniCache = new Map<string, { art: FurniArt; c: HTMLCanvasElement } | null>();
let hoverArt: { art: FurniArt; c: HTMLCanvasElement } | null = null;

function blit(ctx: CanvasRenderingContext2D, c: HTMLCanvasElement, ax: number, ay: number) {
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(c, ax * P, ay * P, c.width * P, c.height * P);
}

function glow(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, color: string, alpha: number) {
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, color);
  g.addColorStop(1, "rgba(0,0,0,0)");
  ctx.save();
  ctx.globalAlpha *= alpha;
  ctx.globalCompositeOperation = "lighter";
  ctx.fillStyle = g;
  ctx.fillRect(x - r, y - r, r * 2, r * 2);
  ctx.restore();
}

/** Flat pieces (rugs, floor neon) lie on the floor under everything else; wall pieces hang behind everything too. */
export const isFlat = (f: Furni) => FLAT_KINDS.has(f.kind) || f.wall !== undefined;

function background(wall: string, floor: string, size: number) {
  const key = `${wall}|${floor}|${size}`;
  let bg = bgCache.get(key);
  if (!bg) bgCache.set(key, (bg = toCanvas(buildBackground(wall, floor, size))));
  return bg;
}

export function drawFloorAndWalls(ctx: CanvasRenderingContext2D, wall: string, floor: string, size: number, hover: [number, number] | null) {
  const bg = background(wall, floor, size);
  blit(ctx, bg, -OX, -OY);
  if (hover) {
    if (!hoverArt) { const art = buildHover(); hoverArt = { art, c: toCanvas(art.bmp) }; }
    const [hx, hy] = iso(hover[0], hover[1]);
    ctx.globalAlpha = 0.85;
    blit(ctx, hoverArt.c, hoverArt.art.x + hx / P, hoverArt.art.y + hy / P);
    ctx.globalAlpha = 1;
  }
}

/** An empty room with a given wall, floor and size, cropped to its art, for shop tiles and the expansion preview. */
export function roomThumb(wall: string, floor: string, size: number): HTMLCanvasElement {
  const bg = background(wall, floor, size);
  const { data, width, height } = bg.getContext("2d")!.getImageData(0, 0, bg.width, bg.height);
  let x0 = width, y0 = height, x1 = 0, y1 = 0;
  for (let y = 0; y < height; y++)
    for (let x = 0; x < width; x++)
      if (data[(y * width + x) * 4 + 3]) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
  const c = document.createElement("canvas");
  c.className = "pix";
  c.width = Math.max(1, x1 - x0 + 1);
  c.height = Math.max(1, y1 - y0 + 1);
  c.getContext("2d")!.drawImage(bg, -x0, -y0);
  return c;
}

/** Depth key used to sort furniture against avatars. */
export const furniDepth = (f: Furni) => f.x + f.w - 1 + f.y + f.h - 1;

/** Art for a piece is built once at tile (0,0) and shifted, so moving furniture is free. */
function art(f: Furni) {
  const key = `${f.kind}|${f.w}|${f.h}|${f.color}|${f.wall ?? ""}|${f.dir ?? 0}`;
  let e = furniCache.get(key);
  if (e === undefined) {
    const a = buildFurni({ ...f, x: 0, y: 0 });
    e = a ? { art: a, c: toCanvas(a.bmp) } : null;
    furniCache.set(key, e);
  }
  return e;
}

export function drawFurni(ctx: CanvasRenderingContext2D, f: Furni, t: number) {
  const e = art(f);
  if (e) blit(ctx, e.c, e.art.x + (f.x - f.y) * 16, e.art.y + (f.x + f.y) * 8);
  const [sx, sy] = iso(f.x + 0.5, f.y + 0.5);
  switch (f.kind) {
    case "neon": {
      const [cx, cy] = iso(f.x + f.w / 2, f.y + f.h / 2);
      glow(ctx, cx, cy, f.w * 40, f.color, 0.25 + Math.sin(t / 250) * 0.08);
      break;
    }
    case "lamp":
      glow(ctx, sx, sy - 80, 46, f.color, 0.55);
      break;
    case "cabinet":
      glow(ctx, sx, sy - 40, 28, f.color, 0.35 + Math.sin(t / 200 + f.x) * 0.12);
      break;
    case "lavalamp":
      glow(ctx, sx, sy - 34, 26, f.color, 0.4 + Math.sin(t / 700 + f.y) * 0.1);
      break;
    case "plasma":
      // the lightning flickers
      glow(ctx, sx, sy - 54, 30, f.color, 0.35 + Math.sin(t / 90 + f.x) * 0.06 + (Math.sin(t / 37) > 0.7 ? 0.1 : 0));
      break;
    case "fishtank": {
      const [cx, cy] = iso(f.x + f.w / 2, f.y + f.h / 2);
      glow(ctx, cx, cy - 50, 44, f.color, 0.22 + Math.sin(t / 900) * 0.04);
      break;
    }
    case "glowstars": {
      const [cx, cy] = f.wall ? iso(0, f.y + f.h / 2) : iso(f.x + f.w / 2, 0);
      glow(ctx, cx, cy - 76, 56, f.color, 0.12 + Math.sin(t / 1200) * 0.04);
      break;
    }
    case "neonsign": {
      const [cx, cy] = f.wall ? iso(0, f.y + f.h / 2) : iso(f.x + f.w / 2, 0);
      glow(ctx, cx, cy - 64, 70, f.color, 0.22 + Math.sin(t / 300) * 0.06);
      break;
    }
    case "neonrose": {
      const [cx, cy] = f.wall ? iso(0, f.y + f.h / 2) : iso(f.x + f.w / 2, 0);
      glow(ctx, cx, cy - 70, 34, f.color, 0.3 + Math.sin(t / 300) * 0.08);
      break;
    }
    case "stringlights": {
      const [cx, cy] = f.wall ? iso(0, f.y + f.h / 2) : iso(f.x + f.w / 2, 0);
      glow(ctx, cx, cy - 84, 60, f.color, 0.2 + Math.sin(t / 400) * 0.05);
      break;
    }
    case "tv": {
      // the screen only lights the room when it faces it
      const d = f.dir ?? 0;
      if (d < 2) glow(ctx, sx + (d ? 4 : -4), sy - 36, 24, "#7ad8ff", 0.25 + (Math.sin(t / 90) > 0.8 ? 0.1 : 0));
      break;
    }
  }
}

/** Green or red tile outlines under a piece being placed. */
export function drawFootprint(ctx: CanvasRenderingContext2D, f: Furni, ok: boolean) {
  ctx.save();
  ctx.fillStyle = ok ? "rgba(90,255,160,.28)" : "rgba(255,70,100,.35)";
  ctx.strokeStyle = ok ? "#5affa0" : "#ff4664";
  ctx.lineWidth = 2;
  ctx.beginPath();
  const up = (p: [number, number], z: number): [number, number] => [p[0], p[1] - z];
  // wall pieces get their spot outlined on the wall, floor pieces on the floor
  const pts = f.wall === 1 ? [up(iso(0, f.y), 20), up(iso(0, f.y + f.h), 20), up(iso(0, f.y + f.h), 96), up(iso(0, f.y), 96)]
    : f.wall === 0 ? [up(iso(f.x, 0), 20), up(iso(f.x + f.w, 0), 20), up(iso(f.x + f.w, 0), 96), up(iso(f.x, 0), 96)]
    : [iso(f.x, f.y), iso(f.x + f.w, f.y), iso(f.x + f.w, f.y + f.h), iso(f.x, f.y + f.h)];
  ctx.moveTo(...pts[0]);
  for (const p of pts.slice(1)) ctx.lineTo(...p);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

/** A pixelated preview of a piece for the shop and inventory. */
export function furniThumb(f: Furni, size = 72): HTMLCanvasElement {
  const e = art({ ...f, x: 0, y: 0 });
  const c = document.createElement("canvas");
  c.className = "pix";
  c.width = e?.c.width ?? 1;
  c.height = e?.c.height ?? 1;
  if (e) c.getContext("2d")!.drawImage(e.c, 0, 0);
  const k = Math.min(size / c.width, size / c.height, 3);
  c.style.width = Math.round(c.width * k) + "px";
  c.style.height = Math.round(c.height * k) + "px";
  return c;
}
